/**
 * "Which suppliers have come back?" for every card on the Requirements board — TWO requests for
 * the whole board, not one per card.
 *
 * ---- Why `/api/resource` and not a `quoflow.*` method ----
 *
 * `rfq.get_rfqs` REQUIRES an `enquiry` argument — measured 2026-08-08, calling it bare answers
 * `TypeError: get_rfqs() missing 1 required positional argument: 'enquiry'`. So there is no
 * board-wide method, and one call per card is exactly what this hook exists to avoid. It therefore
 * joins the residual generic-resource surface documented in `client.ts`/`frappe.ts` as the eighth
 * entry; when a batched method appears, only `queryFn` changes. Filed as a backend ask.
 *
 * ---- What was measured, 2026-08-08, against rfqdev.etoserp.com ----
 *
 * ```
 * GET /api/resource/RFQ?filters=[["enquiry","in",["ENQ-2026-00010",…]]]
 *     &fields=["name","enquiry","status","supplier","supplier_name","sent_at"]
 *   → [{"name":"RFQ-2026-00011","enquiry":"ENQ-2026-00010","status":"Sent",
 *       "supplier_name":"Shiby","sent_at":"2026-08-07 13:31:29.646640"}, …]
 *
 * GET /api/resource/Communication?filters=[["reference_doctype","=","RFQ"],
 *     ["reference_name","in",[…]],["sent_or_received","=","Received"]]
 *   → [{"name":"70pvs1vfma","reference_name":"RFQ-2026-00011", …}]
 * ```
 *
 * The RFQ link field is `enquiry`. It is NOT `opportunity`, which
 * `docs/bosun-doctype-reference.md` still claims — that spelling is rejected outright with
 * `DataError: Field not permitted in query: opportunity`, so the doc is stale, not merely vague.
 *
 * ---- Two failure modes this deliberately does not have ----
 *
 * A failed query surfaces as `error`, and the board renders NO chip rather than `0/N`. Silence
 * that reads as "nobody has answered" would send a purchaser chasing suppliers who already replied
 * — the same defect as the reply panel rendering a 403 as "no replies".
 *
 * Frappe's default `limit_page_length` is 20, so both queries name an explicit limit and report
 * `truncated` when a page comes back full. A silently short page would produce confidently wrong
 * ratios, which is worse than no ratio at all.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { callGet } from './bosun'
import { getResource } from './frappe'
import { trimmed } from './wire'
import { deriveReplyStates, type BoardRfq, type EnquiryReplyState } from '../boardReplies'
import { qk } from './keys'

/** Explicit, because Frappe's own default is 20 and would truncate a busy board in silence. */
const PAGE_LIMIT = 500

/**
 * Ids per request. The `in` filter travels in the query string, and an unbounded board would build
 * a URL long enough for a proxy to reject — chunking keeps every request short WITHOUT capping
 * coverage, so no card is silently left without a chip.
 */
const CHUNK = 80

interface WireBoardRfq {
  name?: string
  enquiry?: string
  status?: string
  supplier_name?: string
  sent_at?: string | null
}

interface WireLinkedReply {
  name?: string
  reference_name?: string
}

const chunk = <T,>(xs: readonly T[], size: number): T[][] => {
  const out: T[][] = []
  for (let i = 0; i < xs.length; i += size) out.push(xs.slice(i, i + size))
  return out
}

/** `supplier_name` carries a leading space on the live wire — the same defect `wire.ts` centralises. */
export function toBoardRfq(w: WireBoardRfq): BoardRfq {
  return {
    name: trimmed(w.name),
    enquiry: trimmed(w.enquiry),
    status: trimmed(w.status),
    supplier_name: trimmed(w.supplier_name),
    // Distinguishes "never sent" from "sent at an unknown time": only `null` means never sent, and
    // the denominator turns on exactly that.
    sent_at: typeof w.sent_at === 'string' && w.sent_at.trim() !== '' ? w.sent_at : null,
  }
}

export interface BoardRepliesData {
  rfqs: BoardRfq[]
  /** RFQ docnames with at least one RECEIVED reply linked to them. */
  repliedRfqNames: Set<string>
  /**
   * RFQ docnames that have a `Supplier Quotation` against them — the AUTHORITATIVE answer to
   * "did this supplier come back?", because a quotation existing is the fact itself rather than
   * something inferred from it.
   *
   * Added 2026-08-13 after a card showed `0/1 replied` on an enquiry that had a supplier reply
   * AND a recorded quotation. The two older signals both missed it: the RFQ's own status was not
   * moved to `Replied` when we created the quotation ourselves, and the reply arrived as a
   * forward with no `reference_name` (`D12`), so no mail was linked either.
   */
  quotedRfqNames: Set<string>
  /** A page came back full, so some RFQs or replies are missing and the ratios cannot be trusted. */
  truncated: boolean
}

/** Every RFQ belonging to the given enquiries. */
export async function fetchBoardRfqs(enquiryIds: readonly string[]): Promise<{ rfqs: BoardRfq[]; truncated: boolean }> {
  const pages = await Promise.all(
    chunk(enquiryIds, CHUNK).map((ids) =>
      getResource<WireBoardRfq>('RFQ', {
        filters: [['enquiry', 'in', ids]],
        fields: ['name', 'enquiry', 'status', 'supplier_name', 'sent_at'],
        limit: PAGE_LIMIT,
      }),
    ),
  )
  return {
    rfqs: pages.flatMap((p) => p.data.map(toBoardRfq)).filter((r) => r.name !== '' && r.enquiry !== ''),
    truncated: pages.some((p) => p.data.length >= PAGE_LIMIT),
  }
}

/**
 * RFQ docnames that have a supplier reply against them.
 *
 * `sent_or_received: 'Received'` is load-bearing, not a tidy-up: our OWN outgoing RFQ and reminder
 * mails link to the same RFQ, so without it every RFQ would count as answered by its own send.
 */
export async function fetchRepliedRfqNames(
  rfqNames: readonly string[],
): Promise<{ names: Set<string>; truncated: boolean }> {
  const pages = await Promise.all(
    chunk(rfqNames, CHUNK).map((names) =>
      getResource<WireLinkedReply>('Communication', {
        filters: [
          ['reference_doctype', '=', 'RFQ'],
          ['reference_name', 'in', names],
          ['sent_or_received', '=', 'Received'],
        ],
        fields: ['name', 'reference_name'],
        limit: PAGE_LIMIT,
      }),
    ),
  )
  const names = new Set<string>()
  for (const page of pages) {
    for (const row of page.data) {
      const ref = trimmed(row.reference_name)
      if (ref !== '') names.add(ref)
    }
  }
  return { names, truncated: pages.some((p) => p.data.length >= PAGE_LIMIT) }
}

/**
 * RFQ docnames that already have a supplier quotation, across the given enquiries.
 *
 * One call per enquiry, the same shape `fetchBoardRfqs` already uses — `get_supplier_quotations`
 * takes an `enquiry` for the same reason `get_rfqs` does.
 *
 * Fails SOFT, to an empty set: this is a third signal on top of two that already work, so losing
 * it must degrade the chip to its previous behaviour, never break the board.
 */
export async function fetchQuotedRfqNames(enquiryIds: readonly string[]): Promise<Set<string>> {
  const names = new Set<string>()
  try {
    const pages = await Promise.all(
      enquiryIds.map((enquiry) =>
        callGet<{ data?: Array<{ rfq?: unknown }> } | Array<{ rfq?: unknown }>>(
          'supplier_quotation',
          'get_supplier_quotations',
          { enquiry },
        ),
      ),
    )
    for (const page of pages) {
      // The live shape is a bare array on some methods and `{data}` on others — accept both
      // rather than depending on which this one is today.
      const rows = Array.isArray(page) ? page : (page?.data ?? [])
      for (const row of rows) {
        const rfq = trimmed(typeof row?.rfq === 'string' ? row.rfq : '')
        if (rfq !== '') names.add(rfq)
      }
    }
  } catch (error) {
    console.warn(`[board] supplier quotations unavailable, reply chips fall back: ${String(error)}`)
  }
  return names
}

export async function fetchBoardReplies(enquiryIds: readonly string[]): Promise<BoardRepliesData> {
  const { rfqs, truncated: rfqsTruncated } = await fetchBoardRfqs(enquiryIds)
  if (rfqs.length === 0) {
    return { rfqs, repliedRfqNames: new Set(), quotedRfqNames: new Set(), truncated: rfqsTruncated }
  }

  // Second request keyed on the FIRST's results — there is no way to ask for "mail linked to any
  // RFQ of these enquiries" in one go, and fetching every Communication to filter locally is what
  // the contract's §3 warns against.
  // Both keyed on the first request's results, and independent of each other, so they run together.
  const [{ names, truncated: repliesTruncated }, quotedRfqNames] = await Promise.all([
    fetchRepliedRfqNames(rfqs.map((r) => r.name)),
    fetchQuotedRfqNames(enquiryIds),
  ])
  return { rfqs, repliedRfqNames: names, quotedRfqNames, truncated: rfqsTruncated || repliesTruncated }
}

export interface BoardRepliesResult {
  byEnquiry: Map<string, EnquiryReplyState>
  error: Error | null
  truncated: boolean
  isPending: boolean
}

/**
 * Reply state for the given enquiries, keyed by enquiry id.
 *
 * Pass only the enquiries where the question is actionable (see `WATCHED_STATUSES`) — that keeps
 * the filter short and leaves closed work free of stale supplier chatter.
 */
export function useBoardReplies(
  enquiryIds: readonly string[],
  options?: { refetchInterval?: number },
): BoardRepliesResult {
  // Sorted so that a re-render which merely reorders the board is not a different cache entry.
  const ids = useMemo(() => [...enquiryIds].sort(), [enquiryIds])

  const query = useQuery({
    queryKey: qk.boardReplies(ids),
    queryFn: () => fetchBoardReplies(ids),
    enabled: ids.length > 0,
    refetchInterval: options?.refetchInterval,
  })

  const byEnquiry = useMemo(() => {
    if (!query.data) return new Map<string, EnquiryReplyState>()
    // `new Date()` is captured per fetch rather than per render: the waiting days should move when
    // the board refreshes, not silently between renders of the same data.
    return deriveReplyStates(query.data.rfqs, query.data.repliedRfqNames, new Date(), query.data.quotedRfqNames)
  }, [query.data])

  return {
    byEnquiry,
    error: (query.error as Error | null) ?? null,
    truncated: query.data?.truncated ?? false,
    isPending: query.isPending,
  }
}
